import { collabStateManager } from '@databases/collab-state-manager';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import {
  type DatabaseStreamHookState,
  useDatabaseStreamState
} from '@databases/database-ai-first-creation-ui';
import { useCallback, useSyncExternalStore } from 'react';
import { useSnapshot } from 'valtio';

import { getAiFieldActivityIndex } from './ai-field-activity-index.ts';
import { useIsAiSummaryFieldEnabled } from './summary/use-is-ai-summary-field-enabled.ts';

export interface UseIsAiFieldCellStreamingOptions {
  contentId: string;
  fieldId: string;
  entryId: string;
  isTargetValueEmpty?: boolean;
}

export interface UseIsAiFieldCellRunActiveOptions extends UseIsAiFieldCellStreamingOptions {
  aiFieldType: string;
}

type UseIsAiFieldRunActiveOptions = Pick<
  UseIsAiFieldCellRunActiveOptions,
  'contentId' | 'aiFieldType' | 'fieldId'
> & {
  /**
   * Reuse the caller's snapshot to avoid another DatabaseStreamState subscription.
   */
  activeStream: DatabaseStreamHookState['activeStream'];
};

export const useIsAiFieldRunActive = ({
  contentId,
  aiFieldType,
  fieldId,
  activeStream
}: UseIsAiFieldRunActiveOptions): boolean => {
  const manager = collabStateManager.getCollabManager(contentId);
  const activityIndex = getAiFieldActivityIndex(manager);
  const subscribe = useCallback(
    (listener: () => void) =>
      activityIndex.subscribeToField({ aiFieldType, fieldId }, listener),
    [activityIndex, aiFieldType, fieldId]
  );
  const getSnapshot = useCallback(
    () => activityIndex.isFieldActive({ aiFieldType, fieldId }),
    [activityIndex, aiFieldType, fieldId]
  );

  const isCollaborativeRunActive = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getSnapshot
  );

  return (
    isCollaborativeRunActive ||
    (activeStream?.kind === 'ai-field' &&
      activeStream.target.aiFieldType === aiFieldType &&
      activeStream.target.fieldId === fieldId)
  );
};

export const useIsAiFieldCellRunActive = ({
  contentId,
  aiFieldType,
  fieldId,
  entryId,
  isTargetValueEmpty = false
}: UseIsAiFieldCellRunActiveOptions): boolean => {
  const manager = collabStateManager.getCollabManager(contentId);
  const activityIndex = getAiFieldActivityIndex(manager);
  const subscribe = useCallback(
    (listener: () => void) =>
      activityIndex.subscribeToCell(
        { aiFieldType, fieldId, entryId, isTargetValueEmpty },
        listener
      ),
    [activityIndex, aiFieldType, fieldId, entryId, isTargetValueEmpty]
  );
  const getSnapshot = useCallback(
    () =>
      activityIndex.isCellActive({
        aiFieldType,
        fieldId,
        entryId,
        isTargetValueEmpty
      }),
    [activityIndex, aiFieldType, fieldId, entryId, isTargetValueEmpty]
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

export const useIsAiFieldCellStreaming = ({
  contentId,
  fieldId,
  entryId,
  isTargetValueEmpty = false
}: UseIsAiFieldCellStreamingOptions): boolean => {
  const isAiSummaryFieldEnabled = useIsAiSummaryFieldEnabled();
  const isCollaborativeRunActive = useIsAiFieldCellRunActive({
    contentId,
    aiFieldType: 'summary',
    fieldId,
    entryId,
    isTargetValueEmpty
  });
  const { activeStream } = useDatabaseStreamState();
  const { activeAiFieldCell } = useSnapshot(aiFirstCreationStreamingState);

  if (isAiSummaryFieldEnabled && isCollaborativeRunActive) {
    return true;
  }

  if (!isAiSummaryFieldEnabled || activeStream?.kind !== 'ai-field') {
    return false;
  }

  const { target } = activeStream;
  if (target.aiFieldType !== 'summary' || target.fieldId !== fieldId) {
    return false;
  }

  if (target.scope === 'entry' && target.entryId !== entryId) {
    return false;
  }
  if (target.scope === 'entries' && !target.entryIds.includes(entryId)) {
    return false;
  }

  return activeAiFieldCell?.fieldId === fieldId && activeAiFieldCell.entryId === entryId;
};
